import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/auth.store';
import { panelVerificationPath } from './utils/panelVerification';
import { useAdminAuthStore } from './store/admin-auth.store';
import PageMeta from './components/ui/PageMeta';
import RouteFallback from './components/ui/RouteFallback';
import ScrollToTop from './components/ui/ScrollToTop';
import ConnectionGuard from './components/ui/ConnectionGuard';
import DeferredWidgets from './components/ui/DeferredWidgets';
import AdminPushBootstrap from './components/AdminPushBootstrap';
import GlobalSiteSchema from './components/seo/GlobalSiteSchema';

// Public shell only — dashboard/admin shells load with their routes
import PublicLayout from './components/layout/PublicLayout';

// Homepage stays in the main chunk for fastest LCP
import HomePage from './pages/public/HomePage';

const UserLayout = lazy(() => import('./components/layout/UserLayout'));
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'));

const FeaturesPage = lazy(() => import('./pages/public/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/public/PricingPage'));
const AboutPage = lazy(() => import('./pages/public/AboutPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const DocumentationPage = lazy(() => import('./pages/public/DocumentationPage'));
const GuidePage = lazy(() => import('./pages/public/GuidePage'));
const PaymentPage = lazy(() => import('./pages/public/PaymentPage'));
const PayScanPage = lazy(() => import('./pages/public/PayScanPage'));
const PosPage = lazy(() => import('./pages/pos/PosPage'));
const AppsPage = lazy(() => import('./pages/public/AppsPage'));
const AppDetailPage = lazy(() => import('./pages/public/AppDetailPage'));
const BlogPage = lazy(() => import('./pages/public/BlogPage'));
const BlogArticlePage = lazy(() => import('./pages/public/BlogArticlePage'));
const BlogCategoryPage = lazy(() => import('./pages/public/BlogCategoryPage'));
const BlogTagPage = lazy(() => import('./pages/public/BlogTagPage'));
const ProgrammaticSeoPage = lazy(() => import('./pages/public/ProgrammaticSeoPage'));

const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const VerifyPhonePage = lazy(() => import('./pages/auth/VerifyPhonePage'));
const TwoFaPage = lazy(() => import('./pages/auth/TwoFaPage'));
const AuthSocialPage = lazy(() => import('./pages/auth/AuthSocialPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));

const DashboardPage = lazy(() => import('./pages/user/DashboardPage'));
const KycPage = lazy(() => import('./pages/user/KycPage'));
const ApiKeysPage = lazy(() => import('./pages/user/ApiKeysPage'));
const WalletPage = lazy(() => import('./pages/user/WalletPage'));
const InvoicesPage = lazy(() => import('./pages/user/InvoicesPage'));
const RequestPaymentPage = lazy(() => import('./pages/user/RequestPaymentPage'));
const TransactionsPage = lazy(() => import('./pages/user/TransactionsPage'));
const WithdrawalsPage = lazy(() => import('./pages/user/WithdrawalsPage'));
const TicketsPage = lazy(() => import('./pages/user/TicketsPage'));
const SettingsPage = lazy(() => import('./pages/user/SettingsPage'));
const ActivityPage = lazy(() => import('./pages/user/ActivityPage'));
const CardsPage = lazy(() => import('./pages/user/CardsPage'));

const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminKycPage = lazy(() => import('./pages/admin/AdminKycPage'));
const AdminApiKeysPage = lazy(() => import('./pages/admin/AdminApiKeysPage'));
const AdminInvoicesPage = lazy(() => import('./pages/admin/AdminInvoicesPage'));
const AdminPaymentRecoveryPage = lazy(() => import('./pages/admin/AdminPaymentRecoveryPage'));
const AdminTransactionsPage = lazy(() => import('./pages/admin/AdminTransactionsPage'));
const AdminWithdrawalsPage = lazy(() => import('./pages/admin/AdminWithdrawalsPage'));
const AdminGasTreasuryPage = lazy(() => import('./pages/admin/AdminGasTreasuryPage'));
const AdminGatewaySettingsPage = lazy(() => import('./pages/admin/AdminGatewaySettingsPage'));
const AdminTicketsPage = lazy(() => import('./pages/admin/AdminTicketsPage'));
const AdminSuspiciousPage = lazy(() => import('./pages/admin/AdminSuspiciousPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminNetworkPage = lazy(() => import('./pages/admin/AdminNetworkPage'));
const AdminPlansPage = lazy(() => import('./pages/admin/AdminPlansPage'));
const AdminTeamPage = lazy(() => import('./pages/admin/AdminTeamPage'));
const AdminLogsPage = lazy(() => import('./pages/admin/AdminLogsPage'));
const AdminLiveChatPage = lazy(() => import('./pages/admin/AdminLiveChatPage'));
const AdminWalletPage = lazy(() => import('./pages/admin/AdminWalletPage'));
const AdminWalletControlPage = lazy(() => import('./pages/admin/AdminWalletControlPage'));
const AdminWalletInfrastructurePage = lazy(() => import('./pages/admin/AdminWalletInfrastructurePage'));
const AdminWalletAuditPage = lazy(() => import('./pages/admin/AdminWalletAuditPage'));
const AdminHdWalletPage = lazy(() => import('./pages/admin/AdminHdWalletPage'));
const AdminReportsPage = lazy(() => import('./pages/admin/AdminReportsPage'));
const AdminUserProfilePage = lazy(() => import('./pages/admin/AdminUserProfilePage'));
const AdminMessagesPage = lazy(() => import('./pages/admin/AdminMessagesPage'));
const AdminPagesPage = lazy(() => import('./pages/admin/AdminPagesPage'));
const AdminMenuPage = lazy(() => import('./pages/admin/AdminMenuPage'));
const AdminAppsPage = lazy(() => import('./pages/admin/AdminAppsPage'));
const AdminBlogPage = lazy(() => import('./pages/admin/AdminBlogPage'));
const AdminProgrammaticSeoPage = lazy(() => import('./pages/admin/AdminProgrammaticSeoPage'));
const AdminCardsPage = lazy(() => import('./pages/admin/AdminCardsPage'));

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

/* ─────────────── Route Guards ─────────────── */

function GuestOnly() {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Outlet />;
  return <Navigate to={panelVerificationPath(user) || '/dashboard'} replace />;
}

function AuthRequired() {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  return isAuthenticated ? (
    <Outlet />
  ) : (
    <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  );
}

function AdminGuestOnly() {
  const { isAuthenticated } = useAdminAuthStore();
  return isAuthenticated ? <Navigate to="/admin" replace /> : <Outlet />;
}

function AdminRequired() {
  const location = useLocation();
  const { isAuthenticated, admin } = useAdminAuthStore();
  if (!isAuthenticated) return <Navigate to="/admin/adminLogin" replace state={{ from: location.pathname + location.search }} />;

  if (admin?.role === 'super_admin') return <Outlet />;

  const path = location.pathname;
  const permissionMap: Array<{ match: RegExp; permission: string }> = [
    { match: /^\/admin\/?$/, permission: 'dashboard' },
    { match: /^\/admin\/wallets/, permission: 'wallets' },
    { match: /^\/admin\/wallet-control/, permission: 'wallets' },
    { match: /^\/admin\/wallet-infrastructure/, permission: 'wallets' },
    { match: /^\/admin\/wallet-audit/, permission: 'wallets' },
    { match: /^\/admin\/users/, permission: 'users' },
    { match: /^\/admin\/kyc/, permission: 'kyc' },
    { match: /^\/admin\/plans/, permission: 'plans' },
    { match: /^\/admin\/api-keys/, permission: 'api_keys' },
    { match: /^\/admin\/invoices/, permission: 'invoices' },
    { match: /^\/admin\/payment-recovery/, permission: 'invoices' },
    { match: /^\/admin\/transactions/, permission: 'transactions' },
    { match: /^\/admin\/withdrawals/, permission: 'withdrawals' },
    { match: /^\/admin\/tickets/, permission: 'tickets' },
    { match: /^\/admin\/live-chat/, permission: 'live_chat' },
    { match: /^\/admin\/suspicious/, permission: 'suspicious' },
    { match: /^\/admin\/network/, permission: 'network' },
    { match: /^\/admin\/team/, permission: 'admins' },
    { match: /^\/admin\/pages/, permission: 'pages' },
    { match: /^\/admin\/apps/, permission: 'apps' },
    { match: /^\/admin\/blog/, permission: 'blog' },
    { match: /^\/admin\/programmatic-seo/, permission: 'pages' },
    { match: /^\/admin\/menu/, permission: 'menu' },
    { match: /^\/admin\/logs/, permission: 'logs' },
    { match: /^\/admin\/settings/, permission: 'settings' },
    { match: /^\/admin\/cards/, permission: 'cards' },
    { match: /^\/admin\/reports/, permission: 'reports' },
  ];

  const needed = permissionMap.find((item) => item.match.test(path))?.permission;
  if (!needed) return <Outlet />;

  const permissions = admin?.permissions || [];
  return permissions.includes(needed) ? <Outlet /> : <Navigate to="/admin" replace />;
}

export default function App() {
  return (
    <ConnectionGuard>
      <GlobalSiteSchema />
      <ScrollToTop />
      <Routes>
        <Route element={<PublicLayout><Outlet /></PublicLayout>}>
          <Route index element={<HomePage />} />
          <Route path="features" element={<Lazy><FeaturesPage /></Lazy>} />
          <Route path="pricing" element={<Lazy><PricingPage /></Lazy>} />
          <Route path="about" element={<Lazy><AboutPage /></Lazy>} />
          <Route path="contact" element={<Lazy><ContactPage /></Lazy>} />
          <Route path="documentation" element={<Lazy><DocumentationPage /></Lazy>} />
          <Route path="docs" element={<Navigate to="/documentation" replace />} />
          <Route path="docs/*" element={<Navigate to="/documentation" replace />} />
          <Route path="guide" element={<Lazy><GuidePage /></Lazy>} />
          <Route path="apps" element={<Lazy><AppsPage /></Lazy>} />
          <Route path="apps/:slug" element={<Lazy><AppDetailPage /></Lazy>} />
          <Route path="blog" element={<Lazy><BlogPage /></Lazy>} />
          <Route path="blog/category/:slug" element={<Lazy><BlogCategoryPage /></Lazy>} />
          <Route path="blog/tag/:tagSlug" element={<Lazy><BlogTagPage /></Lazy>} />
          <Route path="blog/:slug" element={<Lazy><BlogArticlePage /></Lazy>} />
          <Route path="verify-email" element={<Lazy><VerifyEmailPage /></Lazy>} />
          <Route path="auth/social" element={<Lazy><AuthSocialPage /></Lazy>} />
          <Route path="verify-phone" element={<Lazy><VerifyPhonePage /></Lazy>} />
          <Route path="2fa" element={<Lazy><TwoFaPage /></Lazy>} />

          <Route element={<GuestOnly />}>
            <Route path="login" element={<Lazy><LoginPage /></Lazy>} />
            <Route path="register" element={<Lazy><RegisterPage /></Lazy>} />
            <Route path="forgot-password" element={<Lazy><ForgotPasswordPage /></Lazy>} />
            <Route path="reset-password" element={<Lazy><ResetPasswordPage /></Lazy>} />
          </Route>

          {/* CMS + programmatic SEO: /page/{slug} and /{lang}/page/{slug} (must be splat, not :slug) */}
          <Route path="page/*" element={<Lazy><ProgrammaticSeoPage /></Lazy>} />
          <Route path="ar/page/*" element={<Lazy><ProgrammaticSeoPage /></Lazy>} />
          <Route path="zh/page/*" element={<Lazy><ProgrammaticSeoPage /></Lazy>} />
          <Route path="ko/page/*" element={<Lazy><ProgrammaticSeoPage /></Lazy>} />
        </Route>

        <Route element={<AuthRequired />}>
          <Route path="dashboard" element={<Lazy><UserLayout /></Lazy>}>
            <Route index element={<Lazy><DashboardPage /></Lazy>} />
            <Route path="kyc" element={<Lazy><KycPage /></Lazy>} />
            <Route path="api-keys" element={<Lazy><ApiKeysPage /></Lazy>} />
            <Route path="wallet" element={<Lazy><WalletPage /></Lazy>} />
            <Route path="request-payment" element={<Lazy><RequestPaymentPage /></Lazy>} />
            <Route path="invoices" element={<Lazy><InvoicesPage /></Lazy>} />
            <Route path="transactions" element={<Lazy><TransactionsPage /></Lazy>} />
            <Route path="withdrawals" element={<Lazy><WithdrawalsPage /></Lazy>} />
            <Route path="tickets" element={<Lazy><TicketsPage /></Lazy>} />
            <Route path="settings" element={<Lazy><SettingsPage /></Lazy>} />
            <Route path="activity" element={<Lazy><ActivityPage /></Lazy>} />
            <Route path="cards" element={<Lazy><CardsPage /></Lazy>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Route>

        <Route element={<AdminGuestOnly />}>
          <Route path="admin/adminLogin" element={<Lazy><><PageMeta /><AdminLoginPage /></></Lazy>} />
        </Route>

        <Route element={<AdminRequired />}>
          <Route path="admin" element={<Lazy><AdminLayout /></Lazy>}>
            <Route index element={<Lazy><AdminDashboardPage /></Lazy>} />
            <Route path="wallets" element={<Lazy><AdminWalletPage /></Lazy>} />
            <Route path="wallet-control" element={<Lazy><AdminWalletControlPage /></Lazy>} />
            <Route path="wallet-infrastructure" element={<Lazy><AdminWalletInfrastructurePage /></Lazy>} />
            <Route path="wallet-audit" element={<Lazy><AdminWalletAuditPage /></Lazy>} />
            <Route path="hd-wallet" element={<Lazy><AdminHdWalletPage /></Lazy>} />
            <Route path="users" element={<Lazy><AdminUsersPage /></Lazy>} />
            <Route path="users/:userId" element={<Lazy><AdminUserProfilePage /></Lazy>} />
            <Route path="messages" element={<Lazy><AdminMessagesPage /></Lazy>} />
            <Route path="kyc" element={<Lazy><AdminKycPage /></Lazy>} />
            <Route path="api-keys" element={<Lazy><AdminApiKeysPage /></Lazy>} />
            <Route path="invoices" element={<Lazy><AdminInvoicesPage /></Lazy>} />
            <Route path="payment-recovery" element={<Lazy><AdminPaymentRecoveryPage /></Lazy>} />
            <Route path="transactions" element={<Lazy><AdminTransactionsPage /></Lazy>} />
            <Route path="withdrawals" element={<Lazy><AdminWithdrawalsPage /></Lazy>} />
            <Route path="treasury" element={<Lazy><AdminGasTreasuryPage /></Lazy>} />
            <Route path="treasury/settings" element={<Lazy><AdminGatewaySettingsPage /></Lazy>} />
            <Route path="tickets" element={<Lazy><AdminTicketsPage /></Lazy>} />
            <Route path="suspicious" element={<Lazy><AdminSuspiciousPage /></Lazy>} />
            <Route path="network" element={<Lazy><AdminNetworkPage /></Lazy>} />
            <Route path="plans" element={<Lazy><AdminPlansPage /></Lazy>} />
            <Route path="team" element={<Lazy><AdminTeamPage /></Lazy>} />
            <Route path="settings" element={<Lazy><AdminSettingsPage /></Lazy>} />
            <Route path="pages" element={<Lazy><AdminPagesPage /></Lazy>} />
            <Route path="blog" element={<Lazy><AdminBlogPage /></Lazy>} />
            <Route path="programmatic-seo" element={<Lazy><AdminProgrammaticSeoPage /></Lazy>} />
            <Route path="menu" element={<Lazy><AdminMenuPage /></Lazy>} />
            <Route path="apps" element={<Lazy><AdminAppsPage /></Lazy>} />
            <Route path="logs" element={<Lazy><AdminLogsPage /></Lazy>} />
            <Route path="live-chat" element={<Lazy><AdminLiveChatPage /></Lazy>} />
            <Route path="reports" element={<Lazy><AdminReportsPage /></Lazy>} />
            <Route path="cards" element={<Lazy><AdminCardsPage /></Lazy>} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>
        </Route>

        <Route path="pay/scan" element={<Lazy><><PageMeta /><PayScanPage /></></Lazy>} />
        <Route path="pay/:invoiceId" element={<Lazy><><PageMeta /><PaymentPage /></></Lazy>} />
        <Route path="pos" element={<Lazy><><PageMeta /><PosPage /></></Lazy>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Toaster position="bottom-center" containerStyle={{ bottom: 'max(1.5rem, env(safe-area-inset-bottom))' }} />
      <AdminPushBootstrap />
      <DeferredWidgets />
    </ConnectionGuard>
  );
}
